import React, { PropsWithChildren } from 'react';
import { Alert, Box, CircularProgress } from '@mui/material';
import { useSelector } from 'react-redux';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { SignupLayout, SignupStepUnavailable } from '../components';
import { SignupStepContext, useSignupStepGuard } from '../hooks';
import { SignupFunnelStepPath } from '../types';

/**
 * The company, the funnel and the right to be on this step, all settled before the screen behind this
 * renders — it takes the funnel from the context instead of asking for it again. One place rather than four,
 * so no step can be reached on a reading of its own.
 */
export const SignupStepRoute: React.FC<PropsWithChildren<{ step: SignupFunnelStepPath }>> = ({
  step,
  children,
}) => {
  const companyId = useSelector(getCurrentCompanyId);
  const { state: funnel, unavailable, retry } = useSignupStepGuard(step);

  // Every step behind the login is company-scoped, so without a company there is no funnel to read and no
  // call on these screens that would be answered.
  if (companyId == null) {
    return (
      <SignupLayout>
        <Alert severity="error">Your company could not be resolved. Please sign in again.</Alert>
      </SignupLayout>
    );
  }

  if (unavailable) {
    return (
      <SignupLayout>
        <SignupStepUnavailable onRetry={retry} />
      </SignupLayout>
    );
  }

  // No funnel yet: the read is still out, or the guard is moving the client off a step that is not theirs.
  // Neither is a moment to render — a card form in front of somebody who has already paid is the one thing this
  // funnel must never do, and the second case would show a frame of it.
  if (funnel == null) {
    return (
      <SignupLayout>
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      </SignupLayout>
    );
  }

  return (
    <SignupStepContext.Provider value={{ companyId: companyId, funnel: funnel }}>
      {children}
    </SignupStepContext.Provider>
  );
};
